<template>
    <NegociosShell>
        <section class="sp-section sp-portal">
            <div class="sp-container">

                <div v-if="cargando" class="sp-portal__cargando" role="status">
                    <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
                    <span>Cargando tu información…</span>
                </div>

                <template v-else-if="perfil">
                    <div class="sp-portal__head">
                        <div>
                            <span class="sp-eyebrow">Portal de anunciantes</span>
                            <h1 class="sp-h2">Hola, {{ perfil.anunciante.nombre }}</h1>
                            <p class="sp-lead">{{ perfil.anunciante.email }}</p>
                        </div>
                        <button type="button" class="sp-btn sp-btn--outline" @click="salir">
                            <i class="pi pi-sign-out" aria-hidden="true"></i><span>Cerrar sesión</span>
                        </button>
                    </div>

                    <!-- Verificación de correo pendiente -->
                    <div v-if="!perfil.anunciante.verificado" class="sp-verificar sp-card">
                        <span class="sp-icon-badge"><i class="pi pi-envelope" aria-hidden="true"></i></span>
                        <div class="sp-verificar__texto">
                            <h2 class="sp-h3">Confirma tu correo</h2>
                            <p>Ingresa el código de 6 dígitos que enviamos a {{ perfil.anunciante.email }}.</p>
                        </div>
                        <form class="sp-verificar__form" novalidate @submit.prevent="verificar">
                            <InputMask v-model="codigo" mask="9-9-9-9-9-9" aria-label="Código de verificación" />
                            <button type="submit" class="sp-btn sp-btn--primary" :disabled="verificando">
                                <span>Verificar</span>
                            </button>
                            <button type="button" class="sp-enlace" :disabled="verificando" @click="reenviar">
                                Reenviar código
                            </button>
                        </form>
                    </div>

                    <div class="sp-portal__grid">
                        <!-- Estado de la cuenta -->
                        <article class="sp-card">
                            <h2 class="sp-h3">Estado de tu cuenta</h2>
                            <p class="sp-estado" :class="'sp-estado--' + claseEstado">
                                <i class="pi pi-circle-fill" aria-hidden="true"></i>
                                <span>{{ textoEstado }}</span>
                            </p>
                            <p class="sp-portal__ayuda">{{ ayudaEstado }}</p>
                        </article>

                        <!-- Negocio -->
                        <article v-if="perfil.negocio" class="sp-card">
                            <h2 class="sp-h3">Tu negocio</h2>
                            <dl>
                                <div><dt>Razón social</dt><dd>{{ perfil.negocio.razon_social }}</dd></div>
                                <div v-if="perfil.negocio.nombre_comercial">
                                    <dt>Nombre comercial</dt><dd>{{ perfil.negocio.nombre_comercial }}</dd>
                                </div>
                                <div v-if="perfil.negocio.nit"><dt>NIT</dt><dd>{{ nitCompleto }}</dd></div>
                                <div v-if="perfil.negocio.categoria">
                                    <dt>Categoría</dt><dd>{{ perfil.negocio.categoria }}</dd>
                                </div>
                            </dl>
                        </article>

                        <!-- Sedes -->
                        <article v-if="perfil.sedes && perfil.sedes.length" class="sp-card">
                            <h2 class="sp-h3">Tus sedes</h2>
                            <ul class="sp-sedes">
                                <li v-for="sede in perfil.sedes" :key="sede.id">
                                    <i class="pi pi-map-marker" aria-hidden="true"></i>
                                    <div>
                                        <strong>{{ sede.direccion }}</strong>
                                        <span v-if="sede.municipio">{{ sede.municipio }}, {{ sede.departamento }}</span>
                                    </div>
                                </li>
                            </ul>
                            <p class="sp-portal__ayuda">
                                Seway define el área de cobertura de cada sede al activarla.
                            </p>
                        </article>

                        <!-- Facturación -->
                        <article v-if="perfil.facturacion" class="sp-card">
                            <h2 class="sp-h3">Facturación</h2>
                            <dl>
                                <div>
                                    <dt>Correo</dt><dd>{{ perfil.facturacion.email_facturacion }}</dd>
                                </div>
                                <div>
                                    <dt>RUT</dt>
                                    <dd>{{ perfil.facturacion.tiene_rut ? (perfil.facturacion.rut_nombre_original || 'Adjunto') : 'Pendiente' }}</dd>
                                </div>
                            </dl>
                            <label class="sp-rut-mini">
                                <input type="file" accept=".pdf,.jpg,.jpeg,.png" class="sp-rut-mini__input" @change="subirRut" />
                                <i class="pi pi-upload" aria-hidden="true"></i>
                                <span>{{ perfil.facturacion.tiene_rut ? 'Reemplazar RUT' : 'Adjuntar RUT' }}</span>
                            </label>
                        </article>
                    </div>

                    <p class="sp-nota">
                        <i class="pi pi-info-circle" aria-hidden="true"></i>
                        <span>
                            La gestión de ofertas y campañas estará disponible próximamente. Mientras tanto,
                            un asesor puede acompañarte para publicar tu primera oferta.
                        </span>
                    </p>
                </template>

                <div v-else class="sp-card sp-portal__error">
                    <Message severity="warn" :closable="false">
                        No pudimos cargar tu información. Inicia sesión de nuevo.
                    </Message>
                    <button type="button" class="sp-btn sp-btn--primary" @click="salir">
                        <span>Ir a iniciar sesión</span>
                    </button>
                </div>

            </div>
        </section>
    </NegociosShell>
</template>

<script>
import NegociosShell from '../NegociosShell';
import api, { mensajeDeError } from './api';
import { estaAutenticado, cerrarSesion, actualizarAnunciante } from './sesion';
import { aplicarSeo, limpiarSeo } from '../seo';

const TEXTOS_ESTADO = {
    CREADO: {
        texto: 'Cuenta creada',
        clase: 'creado',
        ayuda: 'Confirma tu correo para continuar con la activación.'
    },
    PENDIENTE_VALIDACION: {
        texto: 'En validación',
        clase: 'pendiente',
        ayuda: 'Estamos revisando la información de tu negocio. Te avisaremos por correo.'
    },
    ACTIVO: {
        texto: 'Cuenta activa',
        clase: 'activo',
        ayuda: 'Tu negocio está habilitado para publicar ofertas.'
    },
    INACTIVO: {
        texto: 'Cuenta inactiva',
        clase: 'inactivo',
        ayuda: 'Comunícate con nosotros para reactivar tu cuenta.'
    },
    SUSPENDIDO: {
        texto: 'Cuenta suspendida',
        clase: 'inactivo',
        ayuda: 'Comunícate con nosotros para conocer el motivo.'
    },
    RECHAZADO: {
        texto: 'Registro rechazado',
        clase: 'inactivo',
        ayuda: 'Comunícate con nosotros para revisar tu caso.'
    }
};

export default {
    name: 'PortalNegocios',
    components: { NegociosShell },
    data() {
        return {
            cargando: true,
            verificando: false,
            perfil: null,
            codigo: ''
        };
    },
    computed: {
        estado() {
            return this.perfil && this.perfil.anunciante ? this.perfil.anunciante.estado : 'CREADO';
        },
        infoEstado() {
            return TEXTOS_ESTADO[this.estado] || TEXTOS_ESTADO.CREADO;
        },
        textoEstado() {
            return this.infoEstado.texto;
        },
        claseEstado() {
            return this.infoEstado.clase;
        },
        ayudaEstado() {
            return this.infoEstado.ayuda;
        },
        nitCompleto() {
            if (!this.perfil.negocio.nit) return '';
            return this.perfil.negocio.digito_verificacion
                ? this.perfil.negocio.nit + '-' + this.perfil.negocio.digito_verificacion
                : this.perfil.negocio.nit;
        }
    },
    mounted() {
        aplicarSeo({
            titulo: 'Portal de anunciantes | Seway Plus Negocios',
            descripcion: 'Administra la información de tu negocio en Seway Plus Negocios.'
        });

        if (!estaAutenticado()) {
            this.$router.replace({ path: '/negocios/login' });
            return;
        }

        this.cargarPerfil();
    },
    unmounted() {
        limpiarSeo();
    },
    methods: {
        cargarPerfil() {
            this.cargando = true;

            api.get('negocios/perfil')
                .then((respuesta) => {
                    this.perfil = respuesta.data ? respuesta.data.data : null;
                    if (this.perfil && this.perfil.anunciante) {
                        actualizarAnunciante(this.perfil.anunciante);
                    }
                })
                .catch(() => {
                    this.perfil = null;
                })
                .finally(() => {
                    this.cargando = false;
                });
        },
        verificar() {
            const codigo = (this.codigo || '').replace(/\D/g, '');

            if (codigo.length !== 6) {
                this.$toast.add({ severity: 'warn', summary: 'El código debe tener 6 dígitos', life: 4000 });
                return;
            }

            this.verificando = true;

            api.post('negocios/codigo/verificar', { codigo })
                .then(() => {
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Correo verificado',
                        detail: 'Tu cuenta pasó a validación.',
                        life: 5000
                    });
                    this.codigo = '';
                    this.cargarPerfil();
                })
                .catch((error) => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Código inválido',
                        detail: mensajeDeError(error),
                        life: 5000
                    });
                })
                .finally(() => {
                    this.verificando = false;
                });
        },
        reenviar() {
            this.verificando = true;

            api.post('negocios/codigo/reenviar')
                .then(() => {
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Código reenviado',
                        detail: 'Revisa tu correo electrónico.',
                        life: 5000
                    });
                })
                .catch((error) => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'No pudimos reenviar el código',
                        detail: mensajeDeError(error),
                        life: 5000
                    });
                })
                .finally(() => {
                    this.verificando = false;
                });
        },
        subirRut(evento) {
            const archivo = evento.target.files && evento.target.files[0];
            if (!archivo) return;

            const datos = new FormData();
            datos.append('rut', archivo);

            api.post('negocios/rut', datos)
                .then(() => {
                    this.$toast.add({
                        severity: 'success',
                        summary: 'RUT cargado',
                        detail: 'Guardamos tu documento de forma segura.',
                        life: 5000
                    });
                    this.cargarPerfil();
                })
                .catch((error) => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'No pudimos cargar el RUT',
                        detail: mensajeDeError(error),
                        life: 5000
                    });
                })
                .finally(() => {
                    evento.target.value = '';
                });
        },
        salir() {
            api.post('negocios/logout')
                .catch(() => {
                    // Aunque falle en el servidor, la sesión local se limpia.
                })
                .finally(() => {
                    cerrarSesion();
                    this.$router.push({ path: '/negocios/login' });
                });
        }
    }
};
</script>

<style lang="scss" scoped>
.sp-portal {
    background: var(--sp-surface-alt);
    min-height: 70vh;
}

.sp-portal__cargando {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    padding: 3rem 0;
    color: var(--sp-text-soft);

    i {
        color: var(--sp-orange);
        font-size: 1.3rem;
    }
}

.sp-portal__head {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.75rem;
}

.sp-portal__grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.25rem;
}

.sp-portal__grid dl {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin: 0.9rem 0 0;
}

.sp-portal__grid dl div {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
}

.sp-portal__grid dt {
    color: var(--sp-text-soft);
    font-size: 0.85rem;

    &::after {
        content: ':';
    }
}

.sp-portal__grid dd {
    margin: 0;
    color: var(--sp-navy);
    font-size: 0.85rem;
    font-weight: 600;
    overflow-wrap: anywhere;
}

.sp-estado {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.9rem;
    font-size: 1.05rem;
    font-weight: 700;

    i {
        font-size: 0.7rem;
    }
}

.sp-estado--creado i { color: #9aa8b2; }
.sp-estado--pendiente i { color: var(--sp-orange); }
.sp-estado--activo i { color: #1f9d55; }
.sp-estado--inactivo i { color: #d9342b; }

.sp-portal__ayuda {
    margin-top: 0.5rem;
    color: var(--sp-text-soft);
    font-size: 0.85rem;
}

.sp-verificar {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.5rem;
    border-color: var(--sp-orange);
}

.sp-verificar__texto p {
    margin-top: 0.35rem;
    color: var(--sp-text-soft);
    font-size: 0.9rem;
}

.sp-verificar__form {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
}

.sp-sedes {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 0.9rem;

    li {
        display: flex;
        align-items: flex-start;
        gap: 0.6rem;
    }

    i {
        margin-top: 0.2rem;
        color: var(--sp-orange);
    }

    div {
        display: flex;
        flex-direction: column;
    }

    strong {
        color: var(--sp-navy);
        font-size: 0.9rem;
    }

    span {
        color: var(--sp-text-soft);
        font-size: 0.85rem;
    }
}

.sp-rut-mini {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    margin-top: 1rem;
    padding: 0.55rem 1rem;
    border: 1.5px dashed var(--sp-border);
    border-radius: 999px;
    color: var(--sp-orange);
    font-size: 0.85rem;
    font-weight: 700;
    cursor: pointer;

    &:hover {
        border-color: var(--sp-orange);
        background: var(--sp-orange-50);
    }
}

.sp-rut-mini__input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
}

.sp-enlace {
    background: none;
    border: 0;
    color: var(--sp-orange);
    font-family: inherit;
    font-size: 0.85rem;
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;
}

.sp-portal__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    max-width: 520px;
    margin: 0 auto;
}

.sp-nota {
    display: flex;
    align-items: flex-start;
    gap: 0.7rem;
    margin-top: 1.75rem;
    padding: 1rem 1.15rem;
    background: var(--sp-orange-50);
    border-left: 4px solid var(--sp-orange);
    border-radius: 0 10px 10px 0;
    color: var(--sp-navy);
    font-size: 0.88rem;

    i {
        flex: 0 0 auto;
        margin-top: 0.15rem;
        color: var(--sp-orange);
    }
}

@media (min-width: 768px) {
    .sp-portal__grid {
        grid-template-columns: 1fr 1fr;
    }
}
</style>
